import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Modal } from '../../ui';
import { useGlobalSearch } from '../../hooks/useGlobalSearch';
import type { SearchHit, SearchHitKind } from '../../services/search.service';

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

const KIND_LABEL: Record<SearchHitKind, string> = {
  lead: 'Leads',
  campaign: 'Campanhas',
  client: 'Clientes',
};

const KIND_ORDER: SearchHitKind[] = ['lead', 'campaign', 'client'];

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const { data: hits = [], isLoading } = useGlobalSearch(query);

  useEffect(() => {
    if (!open) {
      setQuery('');
    }
  }, [open]);

  function handleSelect(link: string) {
    onClose();
    navigate(link);
  }

  // group by kind preserving an explicit order
  const groups = hits.reduce<Record<SearchHitKind, SearchHit[]>>(
    (acc, h) => {
      acc[h.kind].push(h);
      return acc;
    },
    { lead: [], campaign: [], client: [] }
  );

  const trimmed = query.trim();
  const hasQuery = trimmed.length >= 2;
  const hasResults = hits.length > 0;

  return (
    <Modal open={open} onClose={onClose} title="Busca global" size="lg">
      <div className="flex flex-col gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-tertiary" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar leads, campanhas, clientes..."
            className="w-full h-10 pl-9 pr-3 rounded-md border border-border bg-canvas text-sm text-ink-primary placeholder:text-ink-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          />
        </div>

        <div className="max-h-[50vh] overflow-y-auto">
          {!hasQuery && (
            <p className="text-sm text-ink-tertiary text-center py-8">
              Digite pelo menos 2 caracteres para buscar
            </p>
          )}
          {hasQuery && isLoading && (
            <p className="text-sm text-ink-tertiary text-center py-8">Buscando...</p>
          )}
          {hasQuery && !isLoading && !hasResults && (
            <p className="text-sm text-ink-tertiary text-center py-8">
              Nenhum resultado para &quot;{query}&quot;
            </p>
          )}
          {hasQuery &&
            !isLoading &&
            hasResults &&
            KIND_ORDER.map((kind) => {
              const items = groups[kind];
              if (items.length === 0) {
                return null;
              }
              return (
                <div key={kind} className="mb-3">
                  <h3 className="text-xs font-medium text-ink-tertiary uppercase tracking-wider px-2 mb-1">
                    {KIND_LABEL[kind]}
                  </h3>
                  <ul>
                    {items.map((h) => (
                      <li key={`${h.kind}-${h.id}`}>
                        <button
                          type="button"
                          onClick={() => handleSelect(h.link)}
                          className="w-full text-left px-2 py-2 rounded hover:bg-subtle flex flex-col"
                        >
                          <span className="text-sm text-ink-primary">{h.title}</span>
                          {h.subtitle && (
                            <span className="text-xs text-ink-tertiary">{h.subtitle}</span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
        </div>
      </div>
    </Modal>
  );
}
